import { Ban, Construction, Inbox, SearchX, ServerCrash, ShieldOff, WifiOff } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '../cn'
import { Button } from '../primitives/button'
import { CopyField } from '../patterns/copy-field'
import { describeError } from '@/api/describe-error'
import type { ApiError } from '@/api/errors'

interface BaseStateProps {
  readonly icon?: ReactNode
  readonly title: ReactNode
  readonly description?: ReactNode
  readonly action?: ReactNode
  readonly className?: string
  readonly compact?: boolean
}

function BaseState({
  icon,
  title,
  description,
  action,
  className,
  compact = false,
}: BaseStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'px-4 py-6' : 'px-6 py-12',
        className,
      )}
    >
      {icon !== undefined && (
        <div
          aria-hidden
          className="mb-3 flex size-11 items-center justify-center rounded-full bg-paper text-muted [&>svg]:size-5"
        >
          {icon}
        </div>
      )}
      <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
      {description !== undefined && (
        <p className="mt-1 max-w-[420px] text-[13px] text-muted">{description}</p>
      )}
      {action !== undefined && (
        <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>
      )}
    </div>
  )
}

/** One next action per screen (FRONTEND_PLAN §6.5). */
export function EmptyState(props: Omit<BaseStateProps, 'icon'> & { readonly icon?: ReactNode }) {
  return <BaseState icon={props.icon ?? <Inbox />} {...props} />
}

/** A backend-blocked feature: neutral, one sentence, no dates. */
export function UnavailableState(props: Omit<BaseStateProps, 'icon'>) {
  return <BaseState icon={<Construction />} {...props} />
}

export interface ErrorStateProps {
  readonly error: ApiError | Error
  readonly onRetry?: () => void
  readonly className?: string
  readonly compact?: boolean
}

function iconFor(error: ApiError | Error): ReactNode {
  if (!('kind' in error)) return <ServerCrash />
  if (error.kind === 'network') return <WifiOff />
  if (error.status === 404) return <SearchX />
  if (error.status === 403) return <ShieldOff />
  if (error.status === 429 || error.status === 503) return <Ban />
  return <ServerCrash />
}

/** Renders any ApiError with the right copy, a retry when it makes sense, and the request id for support. */
export function ErrorState({ error, onRetry, className, compact }: ErrorStateProps) {
  const isApi = 'kind' in error
  const described = isApi
    ? describeError(error)
    : {
        title: 'Something went wrong',
        message: error.message,
        retryable: true,
        requiresLogin: false,
        requestId: undefined,
      }
  return (
    <div role="alert" className={className}>
      <BaseState
        compact={compact}
        icon={iconFor(error)}
        title={described.title}
        description={described.message}
        action={
          <>
            {described.retryable && onRetry !== undefined && (
              <Button variant="primary" onClick={onRetry}>
                Try again
              </Button>
            )}
            {described.requestId !== undefined && (
              <CopyField label="Request ID" value={described.requestId} compact />
            )}
          </>
        }
      />
    </div>
  )
}
